import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';
import { usePermissions } from '../hooks/usePermissions';
import { logDownload } from '../utils/eventLog';
import StorageBackupModal from './StorageBackupModal';

const formatDT = (str) => {
  if (!str) return '-';
  const d = new Date(str);
  if (isNaN(d)) return '-';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function Storage() {
  const [curPath, setCurPath]   = useState(''); // 상대경로 ('' = 루트)
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total }
  const [showMkdir, setShowMkdir] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  // movingItems: 이동 중인 항목들의 배열([{ name, fromPath, isDir }, ...]) — 단일/다중 이동 공용.
  // null이면 이동 모드가 아님.
  const [movingItems, setMovingItems] = useState(null);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  // 파일위치 공유하기 — 다운로드 버튼 왼쪽에 붙는 공유 버튼용 상태. shareItem이 있으면
  // 공유 대상 선택 모달이 열린다(포스트잇 공유 모달과 동일한 UX: 체크박스 다중선택).
  const [shareItem, setShareItem] = useState(null); // { name, isDir } | null
  const [shareUsers, setShareUsers] = useState([]);
  const [shareChecked, setShareChecked] = useState([]);
  const [shareSaving, setShareSaving] = useState(false);
  // 부서 그룹 접기/펼치기 — 기본은 전부 접힘(비어있는 Set)이고, 펼친 부서명만 이 Set에 담는다.
  const [expandedDepts, setExpandedDepts] = useState(() => new Set());
  const toggleShareDept = (dept) => setExpandedDepts(prev => {
    const next = new Set(prev);
    next.has(dept) ? next.delete(dept) : next.add(dept);
    return next;
  });
  // 대표이사/임원은 부서 묶음 없이 바로 노출하고, 나머지는 부서별로 묶는다(2026-09 요청).
  // 대표이사 여부는 database.js가 유지하는 불변조건(대표이사 계정은 항상 position='대표이사')을
  // 그대로 이용하고, 임원 여부는 GET /storage/users/list가 함께 내려주는 positions.rank_level로
  // 판단한다(둘 다 백엔드 쿼리에서 이미 조인해 내려줌).
  const shareGroups = useMemo(() => {
    const direct = [];
    const deptMap = {};
    shareUsers.forEach(u => {
      if (u.position === '대표이사' || u.rank_level === '임원') {
        direct.push(u);
      } else {
        const dept = u.department || '미배정';
        if (!deptMap[dept]) deptMap[dept] = [];
        deptMap[dept].push(u);
      }
    });
    const deptNames = Object.keys(deptMap).sort((a, b) => {
      if (a === '미배정') return 1;
      if (b === '미배정') return -1;
      return a.localeCompare(b, 'ko');
    });
    return { direct, depts: deptNames.map(dept => ({ dept, people: deptMap[dept] })) };
  }, [shareUsers]);
  // 체크박스로 선택한 항목들(현재 폴더 안에서의 이름 집합) — 폴더 이동 시 초기화된다.
  const [selectedNames, setSelectedNames] = useState(() => new Set());
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadMenuRef = useRef(null);

  const user    = JSON.parse(sessionStorage.getItem('user') || '{}');
  // 권한설정 - 세부설정에서 "접근가능 폴더"를 지정해뒀으면 최상위 폴더 목록도 그 목록만 보이도록 필터링
  // (지금까지는 백엔드가 하위 폴더 진입만 막고 최상위 목록 자체는 그대로 다 보여줬음)
  const { canWrite: canWriteMenu, getDetailList } = usePermissions();
  const allowedFolders = getDetailList('storage', 'accessible_folders'); // null=제한없음, 배열이면 그 안에 있는 폴더만
  // 관리자/생산관리자/협력사 역할 개념이 없어졌으므로, 최고관리자 외에는 예외 없이
  // 계정관리 > 권한설정에서 부여한 쓰기 권한을 그대로 따릅니다.
  const canWrite = canWriteMenu('storage');
  // 스토리지 백업은 storage 메뉴 쓰기권한과 별개로 최고관리자/관리자만 사용할 수 있다(백엔드도
  // localOnly+isSuperAdmin으로 동일하게 게이팅 — 여기 프론트 체크는 UI 노출용일 뿐 실제 보안은
  // 백엔드가 담당한다).
  const isAdmin = user.role === 'superadmin' || user.role === 'admin';

  // ── 상태바(ActivityFeedBar) 클릭 → 업로드/삭제된 폴더로 바로가기 ──────────
  // 스토리지 관련 활동은 sub_type에 "그 항목이 있는(있었던) 폴더 경로"가 담겨 온다
  // (백엔드 storage.js 각 라우트에서 기록). 값이 오면 그 경로로 바로 이동한다.
  const feedLocation = useLocation();
  const feedNavigate = useNavigate();
  useEffect(() => {
    const ao = feedLocation.state?.activityOpen;
    if (!ao) return;
    if (typeof ao.subType === 'string') setCurPath(ao.subType);
    feedNavigate(feedLocation.pathname, { replace: true, state: {} });
  }, [feedLocation.state]); // eslint-disable-line

  // 스토리지 백업 안내(StorageBackupReminderAlert)의 "지금 확인" 버튼 → 여기로 이동해오면서
  // openBackup: true를 넘겨준 경우, 백업 모달을 바로 열어준다.
  useEffect(() => {
    if (feedLocation.state?.openBackup) {
      setShowBackupModal(true);
      feedNavigate(feedLocation.pathname, { replace: true, state: {} });
    }
  }, [feedLocation.state]); // eslint-disable-line

  // React JSX만으로는 webkitdirectory 속성이 누락되는 브라우저가 있어 DOM에 직접 설정
  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '');
      folderInputRef.current.setAttribute('directory', '');
      folderInputRef.current.setAttribute('mozdirectory', '');
    }
  }, []);

  // 업로드 메뉴 바깥 클릭 시 닫기
  useEffect(() => {
    if (!showUploadMenu) return;
    const onClickOutside = (e) => {
      if (uploadMenuRef.current && !uploadMenuRef.current.contains(e.target)) setShowUploadMenu(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showUploadMenu]);

  const showMsg = (m, type='success') => { setMsg({ text:m, type }); setTimeout(()=>setMsg(''), 2500); };

  const load = (p = curPath) => {
    setLoading(true);
    API.get('/storage/list', { params: { path: p } })
      .then(r => { setItems(r.data.items); setLoading(false); })
      .catch(err => { showMsg(err.response?.data?.message || '불러오기 실패', 'error'); setLoading(false); });
  };

  useEffect(() => { load(curPath); setSelectedNames(new Set()); }, [curPath]); // eslint-disable-line

  const enterFolder = (name) => {
    setCurPath(p => (p ? p + '/' + name : name));
  };

  // 최상위(루트)에서만 접근가능 폴더 제한을 적용 — 하위 폴더 안에서는 이미 그 폴더에
  // 들어올 수 있었다는 것 자체가 허용된 폴더라는 뜻이므로 그대로 다 보여줌
  const displayItems = (curPath === '' && Array.isArray(allowedFolders))
    ? items.filter(it => !it.isDir || allowedFolders.includes(it.name))
    : items;

  const breadcrumbs = ['D:\\Storage', ...(curPath ? curPath.split('/') : [])];

  const goBreadcrumb = (idx) => {
    if (idx === 0) { setCurPath(''); return; }
    setCurPath(breadcrumbs.slice(1, idx+1).join('/'));
  };

  const handlePickFiles = () => { setShowUploadMenu(false); fileInputRef.current?.click(); };
  const handlePickFolder = () => { setShowUploadMenu(false); folderInputRef.current?.click(); };

  // 파일/폴더 업로드 공용 처리 — 아래 두 경로에서 함께 사용합니다.
  //  1) handleFileChange: 버튼으로 <input type=file>에서 파일/폴더를 선택했을 때
  //     (relPath는 input의 file.webkitRelativePath 사용)
  //  2) handleDrop: 내 PC 탐색기에서 이 화면으로 파일/폴더를 직접 드래그 앤 드롭했을 때
  //     (relPath는 handleDrop이 webkitGetAsEntry로 재귀 탐색해서 만들어줌)
  // rawEntries: [{ file, relPath }, ...] — relPath는 "하위폴더/파일명"(최상위면 파일명만)
  // - 폴더가 포함되어 있으면 relPath로 하위 경로를 그대로 살려서 순차 업로드
  // - 1) 필요한 하위 폴더를 먼저 전부 만들고 2) 그 다음 파일을 순서대로 올림 (누락 방지)
  const processFiles = async (rawEntries) => {
    if (!rawEntries || rawEntries.length === 0) return;

    setUploading(true);

    // 파일별 목적 폴더 경로 계산 (하위 경로가 있을 때만 subDir이 채워짐)
    // topFolder: relPath에 '/'가 있으면(=폴더 구조를 통째로 올리는 경우) 그 최상위 폴더 이름.
    // 폴더 업로드는 파일 하나하나가 아니라 "{폴더명} 등록" 한 줄만 활동피드에 남기기 위해,
    // 이런 파일들은 개별 업로드 로그를 생략(silent)하고 업로드가 끝난 뒤 폴더 단위로 한 번만 기록한다.
    // 2026-10 요청: 드래그 앤 드롭 시 빈 폴더도 업로드 — readEntry가 폴더마다 { isDir:true, relPath } 를 함께 넘긴다.
    const dirOnly = rawEntries.filter(r => r.isDir).map(r => ({ relPath: r.relPath, targetPath: [curPath, r.relPath].filter(Boolean).join('/'), topFolder: r.relPath.split('/')[0] }));
    const fileEntries = rawEntries.filter(r => !r.isDir).map(({ file, relPath }) => {
      const subDir  = relPath.includes('/')
        ? relPath.slice(0, relPath.length - file.name.length - 1)
        : '';
      const targetPath = [curPath, subDir].filter(Boolean).join('/');
      const topFolder = relPath.includes('/') ? relPath.split('/')[0] : null;
      return { file, relPath, targetPath, topFolder };
    });

    // 1) 업로드 전에 필요한 하위 폴더를 깊이가 얕은 순서대로 미리 생성 (이미 있으면 무시)
    const uniqueDirs = Array.from(new Set([...fileEntries, ...dirOnly].map(f => f.targetPath).filter(Boolean)))
      .sort((a, b) => a.split('/').length - b.split('/').length);

    for (const dirPath of uniqueDirs) {
      const parts = dirPath.split('/');
      const parentPath = parts.slice(0, -1).join('/');
      const dirName = parts[parts.length - 1];
      try {
        await API.post('/storage/mkdir', { path: parentPath, name: dirName });
      } catch (err) {
        // 이미 존재하는 폴더면 정상 무시, 그 외 오류만 콘솔에 남김
        const already = err.response?.data?.message?.includes('이미');
        if (!already) console.error('폴더 생성 실패:', dirPath, err.response?.data?.message);
      }
    }

    // 2) 파일 순차 업로드
    setUploadProgress(fileEntries.length > 1 ? { done: 0, total: fileEntries.length } : null);
    // 사이드바의 "💾 스토리지" 메뉴 옆에도 진행률(%)을 함께 보여준다 — 업로드 중 다른 메뉴로
    // 이동해도(사이드바는 페이지 이동에 다시 마운트되지 않는 컴포넌트) 계속 보이도록 window
    // 커스텀 이벤트로 알린다.
    const notifySidebar = (percent) => window.dispatchEvent(new CustomEvent('storage:upload-progress', { detail: { percent } }));
    notifySidebar(0);
    const failedFiles = [];

    for (let i = 0; i < fileEntries.length; i++) {
      const { file, relPath, targetPath, topFolder } = fileEntries[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('path', targetPath);
        if (topFolder) formData.append('silent', 'true'); // 폴더 업로드에 포함된 파일은 개별 로그 생략
        await API.post('/storage/upload', formData);
      } catch (err) {
        failedFiles.push({ name: relPath, reason: err.response?.data?.message || '알 수 없는 오류' });
      }
      if (fileEntries.length > 1) setUploadProgress({ done: i + 1, total: fileEntries.length });
      notifySidebar(Math.round(((i + 1) / fileEntries.length) * 100));
    }

    // 폴더 단위로 업로드된 경우 최상위 폴더 이름으로 활동피드에 한 줄만 남긴다("{폴더명} 등록")
    const uploadedTopFolders = Array.from(new Set([...fileEntries, ...dirOnly].map(f => f.topFolder).filter(Boolean)));
    for (const folderName of uploadedTopFolders) {
      try { await API.post('/storage/log-folder-upload', { path: curPath, name: folderName }); } catch (e) { /* 로그 실패는 무시 */ }
    }

    setUploading(false);
    setUploadProgress(null);
    notifySidebar(null);
    load();

    if (failedFiles.length === 0) {
      if (fileEntries.length === 0) showMsg(`폴더 업로드 완료! (${dirOnly.length}개 폴더)`);
      else showMsg(fileEntries.length > 1 ? `업로드 완료! (${fileEntries.length}개 파일)` : '업로드 완료!');
    } else {
      console.error('업로드 실패한 파일 목록:', failedFiles);
      showMsg(`업로드 완료 (성공 ${fileEntries.length-failedFiles.length}개 / 실패 ${failedFiles.length}개 — 실패 목록은 브라우저 콘솔(F12)에서 확인 가능)`, 'error');
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const rawEntries = files.map(file => ({ file, relPath: file.webkitRelativePath || file.name }));
    processFiles(rawEntries).finally(() => { e.target.value = ''; });
  };

  // ── 드래그 앤 드롭 업로드 ──────────────────────────────────────────
  // 내 PC의 탐색기(파일 탐색기/Finder 등)에서 파일이나 폴더를 화면 위로 그대로 끌어다 놓으면
  // 업로드되는 기능. 드롭한 폴더 안의 하위 폴더 구조도 그대로 살려서 올리기 위해, 브라우저의
  // DataTransferItem.webkitGetAsEntry()로 재귀적으로 내용을 읽어들인 뒤(파일 선택 input의
  // webkitRelativePath와 동일한 모양의 relPath로 변환해서) processFiles로 넘긴다.
  const readEntry = (entry, basePath = '') => new Promise((resolve) => {
    if (entry.isFile) {
      entry.file(
        (file) => resolve([{ file, relPath: basePath + entry.name }]),
        () => resolve([])
      );
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const collected = [];
      // readEntries()는 한 번 호출에 최대 100개까지만 반환하므로, 빈 배열이 돌아올 때까지 반복 호출한다.
      const readBatch = () => {
        reader.readEntries(async (batch) => {
          if (batch.length === 0) {
            const nested = await Promise.all(collected.map(child => readEntry(child, basePath + entry.name + '/')));
            resolve([{ isDir: true, relPath: basePath + entry.name }, ...nested.flat()]); // 빈 폴더도 만들어지도록 폴더 자체도 전달
            return;
          }
          collected.push(...batch);
          readBatch();
        }, () => resolve([{ isDir: true, relPath: basePath + entry.name }]));
      };
      readBatch();
    } else {
      resolve([]);
    }
  });

  const [dragOver, setDragOver] = useState(false);
  const dragCounterRef = useRef(0); // dragenter/dragleave가 자식 요소마다 겹쳐 발생해도 깜빡이지 않도록 카운트로 관리

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (!canWrite || uploading) return;
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    dragCounterRef.current += 1;
    setDragOver(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!canWrite || uploading) return;
    e.dataTransfer.dropEffect = 'copy';
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    if (!canWrite || uploading) return;
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragOver(false);
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragOver(false);
    if (!canWrite || uploading) return;

    const dt = e.dataTransfer;
    if (!dt) return;
    const items = Array.from(dt.items || []);
    let rawEntries = [];
    if (items.length && items[0].webkitGetAsEntry) {
      const entries = items.map(it => it.webkitGetAsEntry()).filter(Boolean);
      const results = await Promise.all(entries.map(en => readEntry(en)));
      rawEntries = results.flat();
    } else {
      // webkitGetAsEntry를 지원하지 않는 환경 — 폴더 구조는 못 살리지만 파일만이라도 업로드
      rawEntries = Array.from(dt.files || []).map(file => ({ file, relPath: file.name }));
    }
    processFiles(rawEntries);
  };

  const handleDownload = (name, isDir) => {
    const targetPath = curPath ? curPath + '/' + name : name;
    const url = isDir ? '/storage/download-folder' : '/storage/download';
    setMsg(''); showMsg(isDir ? '폴더를 압축하는 중... 잠시만 기다려주세요.' : '다운로드 준비 중...');
    API.get(url, { params: { path: targetPath }, responseType: 'blob' })
      .then(res => {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = blobUrl; a.download = isDir ? name + '.zip' : name;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(blobUrl);
        logDownload('스토리지', isDir ? name + '.zip' : name);
        showMsg('다운로드 완료!');
      })
      .catch(() => showMsg('다운로드 실패', 'error'));
  };

  // ── 파일위치 공유하기 ────────────────────────────────────────────────
  const openShareModal = (name, isDir) => {
    setShareItem({ name, isDir });
    setShareChecked([]);
    setExpandedDepts(new Set()); // 열 때마다 부서 그룹은 전부 접힌 상태로 시작
    if (shareUsers.length === 0) {
      API.get('/storage/users/list').then(r => setShareUsers(r.data || [])).catch(() => {});
    }
  };
  const submitShare = async () => {
    if (!shareItem || shareChecked.length === 0) return;
    const targetPath = curPath ? curPath + '/' + shareItem.name : shareItem.name;
    setShareSaving(true);
    try {
      const res = await API.post('/storage/share', {
        path: targetPath, name: shareItem.name, isDir: shareItem.isDir, usernames: shareChecked,
      });
      showMsg(res.data?.message || '공유했습니다.');
      setShareItem(null);
    } catch (err) {
      showMsg(err.response?.data?.message || '공유 실패', 'error');
    } finally {
      setShareSaving(false);
    }
  };

  const handleDelete = async (name, isDir) => {
    if (!window.confirm(`'${name}'${isDir ? ' 폴더' : ''}를 삭제하시겠습니까?${isDir ? ' (하위 파일 전부 삭제됩니다)' : ''}`)) return;
    const target = curPath ? curPath + '/' + name : name;
    try {
      await API.delete('/storage/delete', { params: { path: target } });
      showMsg('삭제 완료!');
      load();
    } catch (err) {
      showMsg(err.response?.data?.message || '삭제 실패', 'error');
    }
  };

  // ── 이름 변경: 파일/폴더명을 바꾼다(같은 폴더 안에서만 — 다른 위치로 옮기려면 "이동" 버튼 사용).
  const handleRename = async (name, isDir) => {
    const input = window.prompt(`새 ${isDir ? '폴더' : '파일'} 이름을 입력하세요.`, name);
    if (input == null) return; // 취소
    const newName = input.trim();
    if (!newName || newName === name) return;
    const target = curPath ? curPath + '/' + name : name;
    try {
      await API.post('/storage/rename', { path: target, newName });
      showMsg('이름 변경 완료!');
      load();
    } catch (err) {
      showMsg(err.response?.data?.message || '이름 변경 실패', 'error');
    }
  };

  const handleMkdir = async () => {
    if (!newFolderName.trim()) return;
    try {
      await API.post('/storage/mkdir', { path: curPath, name: newFolderName.trim() });
      showMsg('폴더 생성 완료!');
      setNewFolderName(''); setShowMkdir(false);
      load();
    } catch (err) {
      showMsg(err.response?.data?.message || '폴더 생성 실패', 'error');
    }
  };

  // ── 이동: 대상(1개 또는 여러개) 선택 → 원하는 폴더로 이동(탐색) → "여기로 이동" 확정
  const startMove = (name, isDir) => {
    const fromPath = curPath ? curPath + '/' + name : name;
    setMovingItems([{ name, fromPath, isDir }]);
  };
  const startMoveMulti = (names) => {
    const list = names.map(name => {
      const it = displayItems.find(x => x.name === name);
      return { name, fromPath: curPath ? curPath + '/' + name : name, isDir: it ? it.isDir : false };
    });
    if (list.length === 0) return;
    setMovingItems(list);
  };
  const cancelMove = () => setMovingItems(null);
  const confirmMoveHere = async () => {
    if (!movingItems || movingItems.length === 0) return;
    const fromDir = movingItems[0].fromPath.split('/').slice(0,-1).join('/') || '';
    if ((curPath || '') === fromDir && movingItems.length === 1) {
      showMsg('이미 이 폴더에 있습니다.', 'error');
      return;
    }
    try {
      if (movingItems.length === 1) {
        await API.post('/storage/move', { from: movingItems[0].fromPath, to: curPath });
        showMsg(`'${movingItems[0].name}' 이동 완료!`);
      } else {
        const r = await API.post('/storage/move-multi', { items: movingItems.map(m => m.fromPath), to: curPath });
        showMsg(r.data?.message || '이동 완료!');
      }
      setMovingItems(null);
      setSelectedNames(new Set());
      load();
    } catch (err) {
      showMsg(err.response?.data?.message || '이동 실패', 'error');
    }
  };

  // ── 체크박스 선택 ──────────────────────────────────────
  const toggleSelect = (name) => {
    setSelectedNames(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };
  const selectAll = () => setSelectedNames(new Set(displayItems.map(it => it.name)));
  const clearSelection = () => setSelectedNames(new Set());
  const selectedList = Array.from(selectedNames);

  // ── 선택 항목 일괄 다운로드(zip) / 이동 / 삭제 ──────────────────
  const bulkDownload = () => {
    if (selectedList.length === 0) return;
    const paths = selectedList.map(name => curPath ? curPath + '/' + name : name);
    setMsg(''); showMsg('선택 항목을 압축하는 중... 잠시만 기다려주세요.');
    API.post('/storage/download-multi', { paths }, { responseType: 'blob' })
      .then(res => {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = blobUrl; a.download = 'storage_selected.zip';
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(blobUrl);
        logDownload('스토리지', `선택항목 ${selectedList.length}개.zip`);
        showMsg('다운로드 완료!');
      })
      .catch(() => showMsg('다운로드 실패', 'error'));
  };
  const bulkMove = () => {
    if (selectedList.length === 0) return;
    startMoveMulti(selectedList);
  };
  const bulkDelete = async () => {
    if (selectedList.length === 0) return;
    if (!window.confirm(`선택한 ${selectedList.length}개 항목을 삭제하시겠습니까? (폴더는 하위 파일 전부 삭제됩니다)`)) return;
    const paths = selectedList.map(name => curPath ? curPath + '/' + name : name);
    try {
      const r = await API.post('/storage/delete-multi', { paths });
      showMsg(r.data?.message || '삭제 완료!');
      setSelectedNames(new Set());
      load();
    } catch (err) {
      showMsg(err.response?.data?.message || '삭제 실패', 'error');
    }
  };

  return (
    <div style={{ padding:24, maxWidth:1690, position:'relative' }}
      onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      {dragOver && (
        <div style={{ position:'fixed', inset:0, background:'rgba(24,95,165,0.08)', border:'3px dashed #185FA5',
          zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
          <div style={{ background:'#fff', borderRadius:12, padding:'26px 44px', boxShadow:'0 4px 20px rgba(0,0,0,0.18)',
            fontSize:16, fontWeight:700, color:'#185FA5', display:'flex', alignItems:'center', gap:10 }}>
            📥 여기에 파일/폴더를 놓아 업로드
          </div>
        </div>
      )}
      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>💾 스토리지</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>서버의 D:\Storage 폴더를 조회하고 파일을 업로드/다운로드합니다.</p>
        </div>
        {canWrite && (
          <div className="hdr-actions" style={{ display:'flex', gap:8 }}>
            <button onClick={() => setShowMkdir(true)}
              style={{ background:'#fff', color:'#185FA5', border:'1px solid #185FA5', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              📁 새 폴더
            </button>
            <div ref={uploadMenuRef} style={{ position:'relative' }}>
              <button onClick={() => !uploading && setShowUploadMenu(v => !v)} disabled={uploading}
                style={{ background: uploading ? '#aaa' : '#185FA5', color:'#fff', border:'none', borderRadius:8,
                  padding:'10px 18px', fontSize:14, fontWeight:600, cursor: uploading ? 'not-allowed' : 'pointer' }}>
                {uploadProgress ? `업로드 중... (${uploadProgress.done}/${uploadProgress.total})` : uploading ? '업로드 중...' : '⬆️ 파일 업로드'}
              </button>
              {showUploadMenu && (
                <div style={{ position:'absolute', top:'calc(100% + 4px)', right:0, background:'#fff',
                  border:'1px solid #e5e7eb', borderRadius:8, boxShadow:'0 4px 12px rgba(0,0,0,0.1)', zIndex:10, minWidth:140, overflow:'hidden' }}>
                  <div onClick={handlePickFiles}
                    style={{ padding:'10px 14px', fontSize:13, cursor:'pointer', color:'#333' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    📄 파일 선택
                  </div>
                  <div onClick={handlePickFolder}
                    style={{ padding:'10px 14px', fontSize:13, cursor:'pointer', color:'#333', borderTop:'1px solid #f0f0f0' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    📂 폴더 선택
                  </div>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" multiple style={{ display:'none' }} onChange={handleFileChange} />
            <input ref={folderInputRef} type="file" multiple style={{ display:'none' }} onChange={handleFileChange} />
            {isAdmin && (
              <button onClick={() => setShowBackupModal(true)}
                style={{ background:'#fff', color:'#0F6E56', border:'1px solid #0F6E56', borderRadius:8,
                  padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                🗄️ 스토리지 백업
              </button>
            )}
          </div>
        )}
        {!canWrite && isAdmin && (
          <button onClick={() => setShowBackupModal(true)}
            style={{ background:'#fff', color:'#0F6E56', border:'1px solid #0F6E56', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
            🗄️ 스토리지 백업
          </button>
        )}
      </div>

      {showBackupModal && <StorageBackupModal onClose={() => setShowBackupModal(false)} />}

      {/* 파일위치 공유 대상 선택 모달 — 포스트잇 공유 모달과 동일한 체크박스 다중선택 UX */}
      {shareItem && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1000 }}
          onClick={() => !shareSaving && setShareItem(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:360, maxHeight:'70vh', display:'flex', flexDirection:'column' }}>
            <h3 style={{ margin:'0 0 4px', fontSize:16, fontWeight:600 }}>파일위치 공유</h3>
            <p style={{ margin:'0 0 12px', fontSize:12, color:'#888' }}>
              {shareItem.isDir ? '📁' : '📄'} {shareItem.name}
            </p>
            <div style={{ flex:1, overflowY:'auto', border:'1px solid #e5e7eb', borderRadius:8, padding:8 }}>
              {shareUsers.length === 0 ? (
                <div style={{ color:'#aaa', fontSize:13, padding:8 }}>불러오는 중...</div>
              ) : (
                <>
                  {/* 대표이사/임원 — 부서 묶음 없이 바로 노출 */}
                  {shareGroups.direct.map(u => (
                    <label key={u.username} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 4px', fontSize:13, cursor:'pointer' }}>
                      <input type="checkbox" checked={shareChecked.includes(u.username)}
                        onChange={e => setShareChecked(cs => (e.target.checked ? [...cs, u.username] : cs.filter(x => x !== u.username)))} />
                      <span>{[u.position, u.name].filter(Boolean).join(' ')}</span>
                    </label>
                  ))}
                  {/* 나머지는 부서별로 묶고 기본은 접어둔다 — 좌측 +를 누르면 펼쳐진다 */}
                  {shareGroups.depts.map(({ dept, people }) => {
                    const expanded = expandedDepts.has(dept);
                    return (
                      <div key={dept}>
                        <div onClick={() => toggleShareDept(dept)}
                          style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 4px', cursor:'pointer',
                            fontSize:13, fontWeight:700, color:'#555' }}>
                          <span style={{ width:14, textAlign:'center', color:'#999' }}>{expanded ? '−' : '+'}</span>
                          <span>{dept}</span>
                          <span style={{ fontSize:11, color:'#aaa', fontWeight:400 }}>({people.length})</span>
                        </div>
                        {expanded && people.map(u => (
                          <label key={u.username} style={{ display:'flex', alignItems:'center', gap:8,
                            padding:'6px 4px 6px 24px', fontSize:13, cursor:'pointer' }}>
                            <input type="checkbox" checked={shareChecked.includes(u.username)}
                              onChange={e => setShareChecked(cs => (e.target.checked ? [...cs, u.username] : cs.filter(x => x !== u.username)))} />
                            <span>{[u.position, u.name].filter(Boolean).join(' ')}</span>
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:16 }}>
              <button onClick={() => setShareItem(null)} disabled={shareSaving}
                style={{ background:'#f1f5f9', border:'1px solid #ddd', borderRadius:8, padding:'8px 16px', fontSize:13, cursor:'pointer' }}>취소</button>
              <button onClick={submitShare} disabled={shareSaving || shareChecked.length === 0}
                style={{ background:'#7C3AED', color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600,
                  cursor: (shareSaving || shareChecked.length === 0) ? 'default' : 'pointer',
                  opacity: (shareSaving || shareChecked.length === 0) ? 0.6 : 1 }}>
                {shareSaving ? '공유 중...' : '공유'}
              </button>
            </div>
          </div>
        </div>
      )}

      {msg && (
        <div style={{ background: msg.type==='error' ? '#fee2e2' : '#d1fae5',
          color: msg.type==='error' ? '#dc2626' : '#065f46',
          padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>
          {msg.text}
        </div>
      )}

      {/* 경로(브레드크럼) — 트리/목록 행 위에서 전체 폭을 차지한다 */}
      <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:14,
        background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:'10px 14px', fontSize:14 }}>
        {breadcrumbs.map((b, i) => (
          <span key={i} style={{ display:'flex', alignItems:'center', gap:6 }}>
            {i > 0 && <span style={{ color:'#ccc' }}>/</span>}
            <span onClick={() => goBreadcrumb(i)}
              style={{ cursor:'pointer', color: i===breadcrumbs.length-1 ? '#333' : '#185FA5',
                fontWeight: i===breadcrumbs.length-1 ? 600 : 500 }}>
              {b}
            </span>
          </span>
        ))}
      </div>

      {/* 이동 중 안내바 — 이동할 폴더로 탐색해 들어간 뒤 '여기로 이동'을 누르면 확정 (1개 또는 여러개 동시) */}
      {movingItems && movingItems.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
          background:'#FEF3C7', border:'1px solid #f59e0b', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
          <span style={{ fontSize:13, color:'#92400E' }}>
            📦 {movingItems.length === 1
              ? <><b>{movingItems[0].name}</b>{movingItems[0].isDir ? ' 폴더' : ''}</>
              : <><b>{movingItems.length}개 항목</b></>}
            를 이동할 위치로 이동해서 원하는 폴더에 들어간 뒤 아래 버튼을 눌러주세요.
          </span>
          <div style={{ display:'flex', gap:8, flexShrink:0 }}>
            <button onClick={confirmMoveHere}
              style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:6,
                padding:'6px 14px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              여기로 이동
            </button>
            <button onClick={cancelMove}
              style={{ background:'#fff', color:'#888', border:'1px solid #ddd', borderRadius:6,
                padding:'6px 14px', fontSize:12, cursor:'pointer' }}>
              취소
            </button>
          </div>
        </div>
      )}

      {/* 새 폴더 입력 */}
      {showMkdir && (
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          <input value={newFolderName} onChange={e=>setNewFolderName(e.target.value)}
            placeholder="새 폴더 이름" autoFocus
            onKeyDown={e => e.key==='Enter' && handleMkdir()}
            style={{ flex:1, maxWidth:300, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14 }} />
          <button onClick={handleMkdir}
            style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
              padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            생성
          </button>
          <button onClick={() => { setShowMkdir(false); setNewFolderName(''); }}
            style={{ background:'#fff', color:'#888', border:'1px solid #ddd', borderRadius:8,
              padding:'9px 16px', fontSize:13, cursor:'pointer' }}>
            취소
          </button>
        </div>
      )}

      {/* 선택 도구모음 — 모두선택/모두해제 + 2개 이상 선택시 일괄 다운로드/이동/삭제 버튼 활성화 */}
      {displayItems.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
          marginBottom:8, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button onClick={selectAll}
              style={{ background:'none', border:'1px solid #ccc', color:'#555', borderRadius:6,
                padding:'5px 11px', fontSize:12, cursor:'pointer' }}>
              모두선택
            </button>
            <button onClick={clearSelection}
              style={{ background:'none', border:'1px solid #ccc', color:'#555', borderRadius:6,
                padding:'5px 11px', fontSize:12, cursor:'pointer' }}>
              모두해제
            </button>
            {selectedList.length > 0 && (
              <span style={{ fontSize:12, color:'#888' }}>{selectedList.length}개 선택됨</span>
            )}
          </div>
          {selectedList.length > 0 && (
            <div style={{ display:'flex', gap:6 }}>
              <button onClick={bulkDownload}
                style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:6,
                  padding:'6px 13px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                ⬇️ 선택 다운로드
              </button>
              {canWrite && (
                <>
                  <button onClick={bulkMove}
                    style={{ background:'#0F6E56', color:'#fff', border:'none', borderRadius:6,
                      padding:'6px 13px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    📦 선택 이동
                  </button>
                  <button onClick={bulkDelete}
                    style={{ background:'#dc2626', color:'#fff', border:'none', borderRadius:6,
                      padding:'6px 13px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    🗑️ 선택 삭제
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* 좌측 디렉토리 트리 + 우측 목록 — 윈도우 탐색기 스타일. 브레드크럼/도구모음이 위로
          빠지면서 트리 패널의 윗변이 정확히 목록(이름/크기/수정일/관리 헤더) 박스의 윗변과
          같은 높이에서 시작한다. */}
      <div style={{ display:'flex', gap:16, alignItems:'flex-start' }}>
      <StorageTree curPath={curPath} onSelect={setCurPath} allowedFolders={allowedFolders} />
      <div style={{ flex:1, minWidth:0 }}>

      {/* 목록 */}
      <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden', background:'#fff' }}>
        <div style={{ display:'grid', gridTemplateColumns:'30px 1fr 100px 150px 330px', padding:'10px 16px',
          background:'#f8fafc', fontSize:13, fontWeight:600, color:'#666', borderBottom:'1px solid #e5e7eb', alignItems:'center' }}>
          <input type="checkbox" style={{ cursor:'pointer' }}
            checked={displayItems.length > 0 && selectedList.length === displayItems.length}
            onChange={e => e.target.checked ? selectAll() : clearSelection()} />
          <span>이름</span><span style={{textAlign:'right'}}>크기</span><span style={{textAlign:'center'}}>수정일</span><span style={{textAlign:'center'}}>관리</span>
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:'50px 0', color:'#aaa', fontSize:14 }}>불러오는 중...</div>
        ) : displayItems.length === 0 ? (
          <div style={{ textAlign:'center', padding:'50px 0', color:'#aaa', fontSize:14 }}>비어있는 폴더입니다.</div>
        ) : displayItems.map(it => (
          <div key={it.name} style={{ display:'grid', gridTemplateColumns:'30px 1fr 100px 150px 330px', padding:'11px 16px',
            fontSize:14, borderBottom:'1px solid #f0f0f0', alignItems:'center',
            background: selectedNames.has(it.name) ? '#f0f6ff' : 'transparent' }}>
            <input type="checkbox" style={{ cursor:'pointer' }}
              checked={selectedNames.has(it.name)}
              onChange={() => toggleSelect(it.name)} />
            <span
              onClick={() => it.isDir && enterFolder(it.name)}
              style={{ cursor: it.isDir ? 'pointer' : 'default', color: it.isDir ? '#185FA5' : '#333',
                fontWeight: it.isDir ? 600 : 400, display:'flex', alignItems:'center', gap:8 }}>
              {it.isDir ? '📁' : '📄'} {it.name}
            </span>
            <span style={{ textAlign:'right', color:'#888' }}>{it.sizeLabel || '-'}</span>
            <span style={{ textAlign:'center', color:'#888', fontSize:12 }}>{formatDT(it.modified)}</span>
            <span style={{ textAlign:'center', display:'flex', gap:6, justifyContent:'center', whiteSpace:'nowrap' }}>
              <button onClick={() => openShareModal(it.name, it.isDir)}
                style={{ background:'none', border:'1px solid #7C3AED', color:'#7C3AED', borderRadius:6,
                  padding:'4px 9px', fontSize:12, cursor:'pointer' }}>
                공유하기
              </button>
              <button onClick={() => handleDownload(it.name, it.isDir)}
                style={{ background:'none', border:'1px solid #185FA5', color:'#185FA5', borderRadius:6,
                  padding:'4px 9px', fontSize:12, cursor:'pointer' }}>
                다운로드
              </button>
              {canWrite && (
                <>
                  <button onClick={() => handleRename(it.name, it.isDir)}
                    style={{ background:'none', border:'1px solid #C99A4B', color:'#C99A4B', borderRadius:6,
                      padding:'4px 9px', fontSize:12, cursor:'pointer' }}>
                    이름변경
                  </button>
                  <button onClick={() => startMove(it.name, it.isDir)}
                    style={{ background:'none', border:'1px solid #0F6E56', color:'#0F6E56', borderRadius:6,
                      padding:'4px 9px', fontSize:12, cursor:'pointer' }}>
                    이동
                  </button>
                  <button onClick={() => handleDelete(it.name, it.isDir)}
                    style={{ background:'none', border:'1px solid #dc2626', color:'#dc2626', borderRadius:6,
                      padding:'4px 9px', fontSize:12, cursor:'pointer' }}>
                    삭제
                  </button>
                </>
              )}
            </span>
          </div>
        ))}
      </div>

      </div>
      </div>
    </div>
  );
}

// ── 좌측 디렉토리 트리 패널 (윈도우 탐색기 스타일) ──────────────────────
// 최상위 폴더는 마운트 시 한 번 불러오고, 각 하위 폴더는 펼칠 때(lazy) /storage/list를
// 재사용해 폴더만 걸러 보여준다 — 별도의 트리 전용 API 없이 기존 목록 조회로 구현.
// 패널 폭은 오른쪽 가장자리를 끌어 사용자가 임의로 조절할 수 있고(윈도우 탐색기와 동일한
// 방식), 조절한 값은 localStorage에 저장해 다음 접속 때도 유지된다.
const TREE_W_KEY = 'ims_storage_tree_width';
const TREE_MIN_W = 160, TREE_MAX_W = 480, TREE_DEFAULT_W = 230;

function StorageTree({ curPath, onSelect, allowedFolders }) {
  const [roots, setRoots] = useState(null);
  const [width, setWidth] = useState(() => {
    const saved = Number(localStorage.getItem(TREE_W_KEY));
    return saved >= TREE_MIN_W && saved <= TREE_MAX_W ? saved : TREE_DEFAULT_W;
  });
  const widthRef = useRef(width);
  useEffect(() => { widthRef.current = width; }, [width]);
  const resizeRef = useRef(null);

  const onResizeMove = useCallback((e) => {
    const r = resizeRef.current;
    if (!r) return;
    const next = Math.min(TREE_MAX_W, Math.max(TREE_MIN_W, r.w0 + (e.clientX - r.x0)));
    setWidth(next);
  }, []);
  const stopResize = useCallback(() => {
    resizeRef.current = null;
    localStorage.setItem(TREE_W_KEY, String(widthRef.current));
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', stopResize);
  }, [onResizeMove]);
  const startResize = (e) => {
    e.preventDefault();
    resizeRef.current = { x0: e.clientX, w0: width };
    window.addEventListener('mousemove', onResizeMove);
    window.addEventListener('mouseup', stopResize);
  };
  useEffect(() => () => {
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', stopResize);
  }, [onResizeMove, stopResize]);

  useEffect(() => {
    API.get('/storage/list', { params: { path: '' } })
      .then(r => {
        let dirs = (r.data.items || []).filter(it => it.isDir);
        if (Array.isArray(allowedFolders)) dirs = dirs.filter(it => allowedFolders.includes(it.name));
        setRoots(dirs);
      })
      .catch(() => setRoots([]));
  }, []); // eslint-disable-line

  return (
    <div style={{ position:'relative', width, flexShrink:0, alignSelf:'flex-start', height:'fit-content' }}>
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10,
        padding:'10px 6px', maxHeight:640, height:'fit-content', overflowY:'auto' }}>
        <div onClick={() => onSelect('')}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 8px', cursor:'pointer', borderRadius:6,
            fontSize:14, fontWeight: curPath === '' ? 700 : 600, color: curPath === '' ? '#185FA5' : '#333',
            background: curPath === '' ? '#e0edfb' : 'transparent', marginBottom:4 }}>
          🗄️ D:\Storage
        </div>
        {roots === null ? (
          <div style={{ padding:'8px 10px', fontSize:12, color:'#aaa' }}>불러오는 중...</div>
        ) : roots.length === 0 ? (
          <div style={{ padding:'8px 10px', fontSize:12, color:'#aaa' }}>폴더가 없습니다.</div>
        ) : roots.map(r => (
          <StorageTreeNode key={r.name} nodePath={r.name} name={r.name} depth={0} curPath={curPath} onSelect={onSelect} />
        ))}
      </div>
      {/* 가로 크기 조절(오른쪽 가장자리) */}
      <div onMouseDown={startResize} title="가로 크기 조절"
        style={{ position:'absolute', top:0, right:-5, width:10, height:'100%', cursor:'ew-resize', zIndex:5 }} />
    </div>
  );
}

function StorageTreeNode({ nodePath, name, depth, curPath, onSelect }) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState(null); // null = 아직 불러온 적 없음(펼칠 때 lazy load)
  const [loading, setLoading] = useState(false);

  const toggle = async (e) => {
    e.stopPropagation();
    if (!expanded && children === null) {
      setLoading(true);
      try {
        const r = await API.get('/storage/list', { params: { path: nodePath } });
        setChildren((r.data.items || []).filter(it => it.isDir));
      } catch {
        setChildren([]);
      }
      setLoading(false);
    }
    setExpanded(v => !v);
  };

  const isActive = curPath === nodePath;

  return (
    <div>
      <div onClick={() => onSelect(nodePath)}
        style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 8px', paddingLeft: 8 + depth * 16,
          cursor:'pointer', borderRadius:6, fontSize:14,
          background: isActive ? '#e0edfb' : 'transparent', color: isActive ? '#185FA5' : '#333',
          fontWeight: isActive ? 600 : 400 }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f5f7fa'; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? '#e0edfb' : 'transparent'; }}>
        <span onClick={toggle}
          style={{ width:14, display:'inline-block', textAlign:'center', color:'#999', fontSize:10, flexShrink:0 }}>
          {loading ? '…' : expanded ? '▾' : '▸'}
        </span>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>📁 {name}</span>
      </div>
      {expanded && (
        <div>
          {children === null ? null : children.length === 0 ? (
            <div style={{ paddingLeft: 8 + (depth+1) * 16, fontSize:12, color:'#bbb', padding:'4px 8px' }}>하위 폴더 없음</div>
          ) : children.map(c => (
            <StorageTreeNode key={c.name} nodePath={nodePath + '/' + c.name} name={c.name} depth={depth+1}
              curPath={curPath} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
}